import React from 'react';
import { View } from 'react-native';
import { styled } from 'dripsy';

import { Touchable, ITouchableProps } from './Touchable';
import { BaseText } from './BaseText';
import { Icon } from './Icon';
import type { ColorType } from '@types';
import { Loader } from './Loader';

export enum ButtonStyleEnum {
  Flat = 'flat',
  Outline = 'outline',
  Ghost = 'ghost',
  Dark = 'dark',
  Danger = 'danger',
  DangerFilled = 'danger-filled',
}

function textColorByStyle(props: IButtonStyleProps): ColorType {
  if (props.disabled) {
    if (props.buttonStyle === ButtonStyleEnum.Flat) {
      return '$text100';
    }
    return '$text80';
  }

  switch (props.buttonStyle) {
    case ButtonStyleEnum.Danger: {
      return '$error300';
    }
    case ButtonStyleEnum.DangerFilled: {
      return '$background0';
    }
    case ButtonStyleEnum.Dark: {
      return '$primary300';
    }
    default: {
      return '$heading800';
    }
  }
}

function buttonBackgroundStyle(props: IButtonStyleProps): ColorType {
  switch (props.buttonStyle) {
    case ButtonStyleEnum.Flat: {
      return props.disabled ? '$primary90' : '$primary300';
    }
    case ButtonStyleEnum.DangerFilled: {
      return '$error300';
    }
    case ButtonStyleEnum.Dark: {
      return '$heading800';
    }
    default: {
      return '$background0';
    }
  }
}

const TouchableContainer = styled(Touchable)(
  (props: IButtonStyleProps) => {
    return {
      backgroundColor: buttonBackgroundStyle(props),
      borderColor: props.disabled ? '$background50' : '$background30',
      borderWidth:
        props.buttonStyle === ButtonStyleEnum.Outline ? 1 : 0,
      height: 54,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
    };
  },
);

const ChildrenContainer = styled(View)({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
});

interface ITextStyleProps {
  color: ColorType;
}

const Text = styled(BaseText)((props: ITextStyleProps) => {
  return {
    color: props.color,
    variant: 'text.medium',
    paddingLeft: '$5',
    paddingRight: '$5',
  };
});

interface IButtonStyleProps
  extends Pick<ITouchableProps, 'disabled'> {
  buttonStyle?: ButtonStyleEnum;
}

export interface IButtonProps
  extends Omit<ITouchableProps, 'type'>,
    IButtonStyleProps {
  iconLeft?: string;
  iconRight?: string;
  isLoading?: boolean;
  isTextChildren?: boolean;
}

export function Button({
  iconLeft,
  iconRight,
  buttonStyle = ButtonStyleEnum.Flat,
  disabled,
  isLoading,
  children,
  isTextChildren = true,
  ...props
}: IButtonProps) {
  const color = textColorByStyle({ disabled, buttonStyle });
  const loaderColor: ColorType =
    buttonStyle === ButtonStyleEnum.Flat
      ? '$heading800'
      : '$primary400';

  const isDisabled = isLoading || disabled;

  return (
    <TouchableContainer
      buttonStyle={buttonStyle}
      disabled={isDisabled}
      {...props}
    >
      {isLoading ? (
        <Loader color={loaderColor} />
      ) : (
        <ChildrenContainer>
          {iconLeft ? (
            <Icon color={color} name={iconLeft} size={22} />
          ) : null}

          {isTextChildren ? (
            <Text color={color}>{children}</Text>
          ) : (
            children
          )}

          {iconRight ? (
            <Icon color={color} name={iconRight} size={22} />
          ) : null}
        </ChildrenContainer>
      )}
    </TouchableContainer>
  );
}
